import { Component, computed, inject, signal } from '@angular/core';
import { MerchantsService } from '../../../core/services/merchants.service';
import { StoresService } from '../../../core/services/stores.service';
import { UserService } from '../../../core/services/user.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { IInvoiceListsDateRange, InvoicingService, IPaymentMethod } from '../../../core/services/invoicing.service';
import { debounceTime, Subject } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { DateRangeModalComponent } from '../../../shared/date-range-modal/date-range-modal.component';
import { format } from 'date-fns';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TextInputComponent } from '../../../shared-with-client/text-input/text-input.component';
import { BsDropdownMenuDirective, BsDropdownModule, BsDropdownToggleDirective } from 'ngx-bootstrap/dropdown';
import { LoaderComponent } from '../../../shared-with-client/loader/loader.component';
import { DatePipe } from '@angular/common';
import { MerchantCurrencyPipe } from '../../../shared/pipes/merchant-currency.pipe';
import { PaymentMethodPipe } from '../../../shared/pipes/payment-method.pipe';
import { SaleDetailsComponent } from '../sale-details/sale-details.component';
import { TeyaService } from '../../../core/services/teya.service';

@Component({
    selector: 'app-payments-list-page',
    imports: [
        ReactiveFormsModule,
        TranslatePipe,
        TextInputComponent,
        BsDropdownModule,
        LoaderComponent,
        RouterLink,
        DatePipe,
        BsDropdownToggleDirective,
        BsDropdownMenuDirective,
        MerchantCurrencyPipe,
        PaymentMethodPipe,
    ],
    templateUrl: './payments-list-page.component.html',
    styleUrl: './payments-list-page.component.scss',
})
export class PaymentsListPageComponent {
    private merchantsService = inject(MerchantsService);
    private storesService = inject(StoresService);
    private userService = inject(UserService);
    private modalService = inject(BsModalService);
    private translate = inject(TranslateService);
    private teyaService = inject(TeyaService);

    private invoicingService = inject(InvoicingService);
    private router = inject(Router);
    private fb = inject(FormBuilder);

    public currentMerchant = toSignal(this.merchantsService.activeMerchant);
    public currentStore = toSignal(this.storesService.getActiveStore());
    public currentUser = toSignal(this.userService.currentUser);

    public loading = signal(true);
    public availableUsers = signal<any[]>([]);

    public payymentsData = signal<{ summary: any; payments: any[] }>({ summary: [], payments: [] });

    public searchForm!: FormGroup;

    public filterDateRanges = this.invoicingService.dateRanges;
    public selectedDateRange = signal(this.filterDateRanges[4]); // Este mês

    public chargingMethods = this.invoicingService.chargingMethods;
    public teyaConnectionStatus = toSignal(this.teyaService.checkOrFetchConnectionStatus(this.currentStore()?.id));
    public paymentMethods = computed(() => {
        const paymentMethods = this.invoicingService.paymentMethods.filter(m => !m.isOnlinePayment);

        const chargingMethods = this.teyaConnectionStatus()?.active ? this.chargingMethods : [];

        return [
            ...paymentMethods.slice(0, -1),
            ...chargingMethods,
            ...paymentMethods.slice(-1),
        ];
    });

    public triggerSearchObservable = new Subject<boolean>();

    ngOnInit(): void {
        this.setupForm();

        this.loadData();

        this.triggerSearchObservable.pipe(debounceTime(50)).subscribe(() => {
            this.loadData();
        });
    }
    ngOnDestroy(): void {
        this.triggerSearchObservable.complete();
    }
    setupForm(): void {
        const parsedDateRange = this.invoicingService.parseDateRange(this.selectedDateRange().value);

        this.searchForm = this.fb.group({
            search: [''],
            paymentMethod: [null],
            createdByUser: [null],
            startDate: [parsedDateRange?.startDate],
            endDate: [parsedDateRange?.endDate],
        });

        this.storesService.getStoreUsers(this.currentStore().id).subscribe((users) => {
            this.availableUsers.set(users);
        });

        this.searchForm
            .get('search')!
            .valueChanges.pipe(debounceTime(500))
            .subscribe(() => {
                // Handle search changes
                this.loadData();
            });
        const otherFields = ['paymentMethod', 'createdByUser', 'startDate', 'endDate'];
        otherFields.forEach((field) => {
            this.searchForm.get(field)!.valueChanges.subscribe(() => {
                // Handle other field changes
                this.triggerSearchObservable.next(true);
            });
        });
    }

    loadData(): void {

        this.loading.set(true);
        this.invoicingService
            .getPayments({
                storeId: this.currentStore().id,
                paymentMethod: (this.searchForm.get('paymentMethod')?.value?.value ?? '') as string,
                search: (this.searchForm.get('search')?.value ?? '') as string,
                createdByUserId: this.searchForm.get('createdByUser')?.value?.id ?? null,
                startDate: this.searchForm.get('startDate')?.value ?? null,
                endDate: this.searchForm.get('endDate')?.value ?? null,
            })
            .subscribe({
                next: (response) => {
                    const payments = (response?.payments ?? []).map((payment: any) => ({
                        ...payment,
                        lineKind: 'payment',
                        sortAt: payment.createdAt,
                    }));
                    const cancellations = (response?.cancellations ?? []).map((cancellation: any) => ({
                        ...cancellation,
                        lineKind: 'cancellation',
                        sortAt: cancellation.createdAt,
                    }));
                    const rows = [...payments, ...cancellations].sort(
                        (a, b) => new Date(b.sortAt).getTime() - new Date(a.sortAt).getTime(),
                    );

                    this.payymentsData.set({
                        summary: response?.summary,
                        payments: rows,
                    });
                    this.loading.set(false);
                },
                error: () => {
                    // In case of error, assume there's no overlap
                    this.loading.set(false);
                },
            });
    }

    setDateRange(range: any) {
        if (range.value === IInvoiceListsDateRange.CUSTOM) {
            this.openCustomRange(range);
        } else {
            this.selectedDateRange.set(range);
            const parsedRange = this.invoicingService.parseDateRange(range.value);
            this.searchForm.patchValue({
                startDate: parsedRange?.startDate,
                endDate: parsedRange?.endDate,
            });
        }
    }

    setPaymentMethod(method: any) {
        if (method === 'online') {
            method = { value: 'online', label: this.translate.instant('PAYMENTS_LIST_PAGE.paymentsOnline'), isOnlinePayment: true };
        }
        this.searchForm.patchValue({
            paymentMethod: method,
        });
    }

    setCreatedByUser(user: any) {
        this.searchForm.patchValue({
            createdByUser: user,
        });
    }

    openCustomRange(range: any) {
        const initialState = {
            data: {
                dateInit: null,
                dateEnd: null,
            },
        };

        this.modalService.show(DateRangeModalComponent, { initialState, class: 'modal-md' });

        const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
            if (reason && typeof reason === 'object') {
                const { dateInit, dateEnd } = reason as { dateInit: Date; dateEnd: Date };
                if (dateInit && dateEnd) {
                    this.searchForm.patchValue({
                        startDate: format(dateInit, 'yyyy-MM-dd'),
                        endDate: format(dateEnd, 'yyyy-MM-dd'),
                    });
                }
                this.selectedDateRange.set(range);
            }
            modalCallback.unsubscribe();
        });
    }

    newPayment(): void {
        this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/payment/create');
    }

    openSaleDetails(sale: any): void {
        this.modalService.show(SaleDetailsComponent, { initialState: { sale }, class: 'modal-lg' });
        const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
            if (reason && reason === 'updated') {
                this.loadData();
            }
            modalCallback.unsubscribe();
        });
    }
}
